import * as React from 'react';
import { useState, useEffect } from 'react';
import { useLoaderData, useParams, useNavigate } from 'react-router-dom';
import { ApiHelper } from '../ApiHelper';
import '../styles/common.css'; // Import CSS styles
import '../styles/AuditorView.css'; // Import AuditorView specific styles
import 'react-date-range/dist/styles.css'; // Import the default styles
import 'react-date-range/dist/theme/default.css'; // Import the default theme styles
import 'font-awesome/css/font-awesome.min.css';
import { GraphView, GraphViewButtons } from './GraphView.tsx';
import { ListView } from './ListView.tsx';
import { EventListView } from './EventView.tsx';
import { Tooltip } from "@fluentui/react-components";
import { patchCommitDetails } from './CommitDetails';
import { getFarmNameOptions } from '../utils/constants';
import { useConfig } from "../configContext";
import type { CommitDetails } from '../interfaces/CommitDetails';
import type { Environment, Service } from '../interfaces/Service';

function getServiceEnvironments(service: Service | undefined): Environment[] {
    return service?.Environments ?? Object.values(service?.environments ?? {});
}

export function CommitsView({ auditorView }) {
    const { commits, navState } = useLoaderData() as { commits: CommitDetails[]; navState: any };
   
    const { serviceName } = useParams();
    const config = useConfig();
    const service = config.services.find((s: Service) => s.name === serviceName) as Service | undefined;
    const navigate = useNavigate();
    const basePath = `/service/${serviceName}`;
    const path = auditorView ? `${basePath}/allcommits` : `${basePath}/mycommits`;

    useEffect(() => {
        const titleSuffix = auditorView ? 'All Commits' : 'My Commits';
        window.document.title = `PR Guardian - ${titleSuffix}`;
    }, [auditorView]);

    useEffect(() => {
        if (commits && commits.length === 1 && navState.page === 1) {
            navigate(`${basePath}/CommitDetails/${commits[0].commitId}`, { replace: true });
        }
    }, [commits, navigate, navState.page, basePath]);

    return (
        <div className='ListView'>
            <div className='containerStyles'>
                <TopNavControls navState={navState} items={commits} auditorView={auditorView} onFilterChange={onFilterChange} onViewChange={() => onViewChange(service)} updateStateAndNavigate={updateStateAndNavigate} service={service} />
            </div>
            {(commits && commits.length > 0 && !navState.graphView) && (
                (navState.startDate != null || navState.endDate != null) && service?.name === 'SPO'
                    ? <EventListView commits={commits} auditorView={auditorView} navState={navState} />
                    : <ListView service={service} commits={commits} auditorView={auditorView} />
            )}
            {commits && commits.length > 0 && navState.graphView && <GraphView commits={commits} auditorView={auditorView} rowSize={4} navState={navState} service={service} />}
            {
                !(commits && commits.length) && <div className="no-commits-message">
                    <div className="no-commits-icon">?</div>
                    <div className="no-commits-title">No commits found</div>
                    <div className="no-commits-subtitle">Try adjusting your filters or switching to a different service</div>
                </div>
            }
            <hr className="thin-divider" />
            <BottomNavControls navState={navState} items={commits} onPageChange={onPageChange} />
        </div>
    );

    function onPageChange(direction) {
        let newState = { ...navState };
        newState.page += direction;
        if (newState.page === 1) {
            newState.previousPage = 0;
            newState.boundaryCommitId = null;
        } else {
            const boundaryItem = (commits && commits.length) ? (direction === 1 ? commits[commits.length - 1] : commits[0]) : null;
            if (boundaryItem) {
                newState.boundaryCommitId = boundaryItem.commitId;
            } // else use the one we navigated from
            newState.previousPage = direction === -1;
        }

        navigate(path + '?' + commitsQueryToQueryString(newState, false));
    }

    function onViewChange(service: Service | undefined) {
        let newState = { ...navState };
        const serviceEnvironments = getServiceEnvironments(service);
        const environmentIds = serviceEnvironments.map(env => env.id);
        const highestEnvId = Math.max(...environmentIds, 0);

        newState.page = 1;
        newState.boundaryCommitId = null;
        newState.previousPage = 0;
        newState.graphView = newState.graphView ? null : '1';
        if (newState.graphView) {
            newState.metric = service.defaultConfig.metricId.toString();
            newState.deployedTo = service.defaultConfig.environmentId;
            newState.farmType = highestEnvId + 1; //All environmentIds
            newState.process = service.defaultConfig.processId.toString();
        } else {
            newState.metric = '';
            newState.deployedTo = 0;
        }
        navigate(path + '?' + commitsQueryToQueryString(newState, false));
    }

    function onFilterChange(newAsUser, newCommitId, newBuildNumber, newControlId, newStartDate, newEndDate, newPageSize, newRegressionsOnly, newFarmType) {
        let newState = { ...navState };
        newState.page = 1;
        newState.boundaryCommitId = null;
        newState.previousPage = 0;
        newState.asUser = newAsUser ?? null;
        newState.commitId = newCommitId ?? null;
        newState.buildNumber = newBuildNumber ?? null;
        newState.controlId = newControlId ?? null;
        newState.startDate = newStartDate ?? null;
        newState.endDate = newEndDate ?? null;
        newState.pageSize = newPageSize ?? newState.pageSize;
        newState.regressionsOnly = newRegressionsOnly;
        newState.farmType = newFarmType ?? 0;

        if (newState.commitId) {
            navigate(`${basePath}/CommitDetails/` + newState.commitId);
        } else {
            navigate(path + '?' + commitsQueryToQueryString(newState, false));
        }
    }

    function updateStateAndNavigate(changeAction, resetPagination) {
        let newState = { ...navState };
        if (resetPagination) {
            newState.page = 1;
            newState.boundaryCommitId = null;
            newState.previousPage = 0;
        }

        changeAction(newState);
        navigate(path + '?' + commitsQueryToQueryString(newState, false));
    }
}

// Search Controls Component
function SearchControls({ filterType, setFilterType, inputValue, setInputValue, isDropdownClicked, setIsDropdownClicked, setAsUser, setBuildNumber, setControlId, setCommitId }) {
    const handleFilterTypeChange = (value) => {
        setFilterType(value);
        setInputValue('');
    };

    const handleInputChange = (value) => {
        setInputValue(value);
        if (filterType === 'asUser') {
            setAsUser(value);
            setBuildNumber(null);
            setControlId(null);
        } else if (filterType === 'commitId') {
            setCommitId(value);
        } else if (filterType === 'buildId') {
            setAsUser(null);
            setBuildNumber(value);
            setControlId(null);
        } else if (filterType === 'ksId' || filterType === 'flightId') {
            setAsUser(null);
            setBuildNumber(null);
            setControlId(value);
        } else {
            setAsUser(null);
            setBuildNumber(null);
            setControlId(null);
        }
    };

    const getPlaceholder = () => {
        const placeholders = {
            asUser: 'e.g. prguardian',
            commitId: 'e.g. 32f5a20d76b6de0aebdca958d59087c324c9b594',
            buildId: 'e.g. 1.0.01234.1234',
            ksId: 'e.g. 978C33F5-A589-4F87-A496-97503F12345',
            flightId: 'e.g. Reserved_Server_ExpFeature0000'
        };
        return placeholders[filterType] || 'Search';
    };

    return (
        <>
            <select className="filter-dropdown auditor-filter-dropdown"
                onChange={(e) => handleFilterTypeChange(e.target.value)}
                onClick={() => {
                    setInputValue('');
                    setIsDropdownClicked(true);
                }}>
                <option value="defaultfilter" disabled={isDropdownClicked} hidden>Search</option>
                <option value="asUser">User Alias</option>
                <option value="commitId">Commit Id</option>
                <option value="buildId">Build Number</option>
                <option value="ksId">KillSwitch Id</option>
                <option value="flightId">Flight Id</option>
            </select>

            <input
                type="text"
                name="inputtext"
                className="search-input auditor-search-input"
                placeholder={getPlaceholder()}
                onChange={(e) => handleInputChange(e.target.value)}
                value={inputValue}
            />
        </>
    );
}

// Date Range Controls Component
function DateRangeControls({ startDate, setStartDate, endDate, setEndDate, farmType, setFarmType, navState, service }) {
    const farmNameOptions = getFarmNameOptions(service);

    // Calculate the number of environment-level options dynamically
    const environments = getServiceEnvironments(service);
    const highestEnvId = environments.length > 0 ? Math.max(...environments.map(env => env.id), 0) : 5;
    const envLevelOptionsCount = highestEnvId + 1; // +1 for combined "All Farms"

    // Create array of environment-level option values [1, 2, ..., envLevelOptionsCount]
    const envLevelValues = Array.from({ length: envLevelOptionsCount }, (_, i) => i + 1);

    return (
        <>
            <label className="filter-dropdown auditor-farm-label">Farm Name</label>
            <select className="farm-filter-dropdown auditor-farm-dropdown"
                defaultValue={4}
                onChange={(e) => setFarmType(Number(e.target.value))}
                disabled={navState.graphView || service?.name !== 'SPO'}
                value={navState.deployedTo === 0 ? farmType : navState.deployedTo}>
                {farmNameOptions.filter(option => {
                    if (navState.deployedTo === 0) {
                        return envLevelValues.indexOf(option.value) === -1;
                    } else {
                        return envLevelValues.indexOf(option.value) !== -1;
                    }
                }).map(option => (
                    <option key={option.key} value={option.value}>
                        {option.text}
                    </option>
                ))}
            </select>

            <div className="auditor-date-group">
                <Tooltip relationship="description" positioning="below" content="Based on the event date (deployment date, killswitch/flight activation date) and on Coordinated Universal Time (UTC)">
                    <label className="date-filter-dropdown">Start Date</label>
                </Tooltip>
                <input
                    className="date-search-input auditor-date-input"
                    type="date"
                    onChange={(date) => setStartDate(date.target.value)}
                    value={startDate}
                />
            </div>

            <div className="auditor-date-group">
                <Tooltip relationship="description" positioning="below" content="Based on the event date (deployment date, killswitch/flight activation date) and on Coordinated Universal Time (UTC)">
                    <label className="date-filter-dropdown">End Date</label>
                </Tooltip>
                <input
                    className="date-search-input auditor-date-input"
                    type="date"
                    onChange={(date) => setEndDate(date.target.value)}
                    value={endDate}
                />
            </div>
        </>
    );
}

// Action Buttons Component
function ActionButtons({ regressionsOnly, setRegressionsOnly, onReset, onUpdate }) {
    return (
        <>
            <button
                onClick={() => setRegressionsOnly(!regressionsOnly)}
                className={regressionsOnly ? "topbutton-regression-checked" : "topbutton-regression"}
            >
                Regression Only
            </button>
            <button onClick={onReset} className="topbutton-white">
                Reset
            </button>
            <button onClick={onUpdate} className="topbutton">
                Search
            </button>
        </>
    );
}

function TopNavControls(props) {
    // Destructure props for better dependency tracking in useEffect
    const { navState, onFilterChange, service } = props;

    const [asUser, setAsUser] = useState(props.asUser);
    const [commitId, setCommitId] = useState(props.commitId);
    const [buildNumber, setBuildNumber] = useState(props.buildNumber);
    const [controlId, setControlId] = useState(props.controlId);
    const [startDate, setStartDate] = useState(props.startDate);
    const [endDate, setEndDate] = useState(props.endDate);
    const [pageSize] = useState(props.pageSize);
    const [regressionsOnly, setRegressionsOnly] = useState(navState.regressionsOnly);
    const [filterType, setFilterType] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [isDropdownClicked, setIsDropdownClicked] = useState(false);
    const [isDateOptionClicked, setIsDateOptionClicked] = useState(false);
    const [farmType, setFarmType] = useState(props.farmType);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const farmNameOptions = getFarmNameOptions(service);

    // Calculate the number of environment-level options dynamically
    const environments = getServiceEnvironments(service);
    const highestEnvId = environments.length > 0 ? Math.max(...environments.map(env => env.id), 0) : 5;
    const envLevelOptionsCount = highestEnvId + 1; // +1 for combined "All Farms"
    const envLevelValues = Array.from({ length: envLevelOptionsCount }, (_, i) => i + 1);

    useEffect(() => {
        if (!navState.graphView && isDateOptionClicked) {
            onFilterChange(asUser, commitId, buildNumber, controlId, startDate, endDate, pageSize, regressionsOnly, farmType);
        }
    }, [navState.graphView, isDateOptionClicked, onFilterChange, asUser, commitId, buildNumber, controlId, startDate, endDate, pageSize, regressionsOnly, farmType]);

    const handleReset = () => {
        setIsDateOptionClicked(false);
        setInputValue('');
        setIsDropdownClicked(false);
        setAsUser(null);
        setBuildNumber(null);
        setControlId(null);
        setStartDate(null);
        setEndDate(null);
        setRegressionsOnly(false);
        setShowAdvancedSearch(false);
        setFarmType(null);
    };

    const handleUpdate = () => {
        props.onFilterChange(asUser, commitId, buildNumber, controlId, startDate, endDate, pageSize, regressionsOnly, farmType);
    };

    return (
        <>
            <div className="topnav-container">
                {/* GraphView Layout - List view button (left) | Two rows of filters (middle) | Action buttons (right) */}
                {props.navState.graphView && (
                <div className="graphview-layout">
                    {/* Left: List view button */}
                    <div className="graphview-left-button">
                        <button id="view-button"
                            className="topbutton view-button"
                            onClick={() => { props.onViewChange() }}>
                            List view
                        </button>
                    </div>

                    {/* Middle Row 1: Environment/Process/Metric dropdowns + Advanced Search button */}
                    <div className="graphview-row-top">
                        <GraphViewButtons
                            navState={props.navState}
                            updateStateAndNavigate={props.updateStateAndNavigate}
                            showAdvancedSearch={showAdvancedSearch}
                            setShowAdvancedSearch={setShowAdvancedSearch}
                        />
                    </div>

                    {/* Middle Row 2: Search and event controls - conditionally rendered */}
                    {showAdvancedSearch && <div className="graphview-row-bottom">
                        {!isDateOptionClicked && (
                            <>
                                <SearchControls
                                    filterType={filterType}
                                    setFilterType={setFilterType}
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    isDropdownClicked={isDropdownClicked}
                                    setIsDropdownClicked={setIsDropdownClicked}
                                    setAsUser={setAsUser}
                                    setBuildNumber={setBuildNumber}
                                    setControlId={setControlId}
                                    setCommitId={setCommitId}
                                />

                                <button className="date-select-button auditor-date-button"
                                    onClick={() => {
                                        setIsDateOptionClicked(true);
                                        setFarmType(4);
                                    }}
                                    disabled={inputValue !== '' || props.service?.name !== 'SPO'}>
                                    Select event date range
                                </button>
                            </>
                        )}

                        {isDateOptionClicked && (
                            <DateRangeControls
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                                farmType={farmType}
                                setFarmType={setFarmType}
                                navState={props.navState}
                                service={props.service}
                            />
                        )}
                    </div>}

                    {/* Right: Action buttons */}
                    <div className="graphview-right-buttons">
                        <ActionButtons
                            regressionsOnly={regressionsOnly}
                            setRegressionsOnly={setRegressionsOnly}
                            onReset={handleReset}
                            onUpdate={handleUpdate}
                        />
                    </div>
                </div>
            )}

            {/* List View Layout */}
            {!props.navState.graphView && (
                <div className="filter-container auditor-filters">
                    <div className="auditor-filter-half">
                        <button id="view-button"
                            className="topbutton view-button"
                            onClick={() => { props.onViewChange() }}>
                            Graph view
                        </button>

                        {!isDateOptionClicked && (
                            <div className="search-set">
                                <SearchControls
                                    filterType={filterType}
                                    setFilterType={setFilterType}
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    isDropdownClicked={isDropdownClicked}
                                    setIsDropdownClicked={setIsDropdownClicked}
                                    setAsUser={setAsUser}
                                    setBuildNumber={setBuildNumber}
                                    setControlId={setControlId}
                                    setCommitId={setCommitId}
                                />
                            </div>
                        )}

                        {isDateOptionClicked && (
                            <div className="search-set">
                                <label className="filter-dropdown auditor-farm-label">Farm Name</label>
                                <select className="farm-filter-dropdown auditor-farm-dropdown"
                                    defaultValue={4}
                                    onChange={(e) => setFarmType(Number(e.target.value))}
                                    disabled={navState.graphView || service?.name !== 'SPO'}
                                    value={navState.deployedTo === 0 ? farmType : navState.deployedTo}>
                                    {farmNameOptions.filter(option => {
                                        if (navState.deployedTo === 0) {
                                            return envLevelValues.indexOf(option.value) === -1;
                                        } else {
                                            return envLevelValues.indexOf(option.value) !== -1;
                                        }
                                    }).map(option => (
                                        <option key={option.key} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="auditor-filter-half">
                        {!isDateOptionClicked && (
                            <button className="date-select-button auditor-date-button"
                                onClick={() => {
                                    setIsDateOptionClicked(true);
                                    setFarmType(4);
                                }}
                                disabled={inputValue !== '' || props.service?.name !== 'SPO'}>
                                Select a event date range by a farm name (optional):
                            </button>
                        )}

                        {isDateOptionClicked && (
                            <div className="auditor-date-container">
                                <div className="auditor-date-group">
                                    <Tooltip relationship="description" positioning="below" content="Based on the event date (deployment date, killswitch/flight activation date) and on Coordinated Universal Time (UTC)">
                                        <label className="date-filter-dropdown">Start Date</label>
                                    </Tooltip>
                                    <input
                                        className="date-search-input auditor-date-input"
                                        type="date"
                                        onChange={(date) => setStartDate(date.target.value)}
                                        value={startDate}
                                        disabled={props.service?.name !== 'SPO'}
                                    />
                                </div>

                                <div className="auditor-date-group">
                                    <Tooltip relationship="description" positioning="below" content="Based on the event date (deployment date, killswitch/flight activation date) and on Coordinated Universal Time (UTC)">
                                        <label className="date-filter-dropdown">End Date</label>
                                    </Tooltip>
                                    <input
                                        className="date-search-input auditor-date-input"
                                        type="date"
                                        onChange={(date) => setEndDate(date.target.value)}
                                        value={endDate}
                                        disabled={props.service?.name !== 'SPO'}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="auditor-filter-buttons">
                        <ActionButtons
                            regressionsOnly={regressionsOnly}
                            setRegressionsOnly={setRegressionsOnly}
                            onReset={handleReset}
                            onUpdate={handleUpdate}
                        />
                    </div>
                </div>
            )}
        </div>
        </>
    );
}

function BottomNavControls(props) {
    return (
        <div className="bottom-nav-controls">
            <div className="bottom-nav-left">
                <button
                    className={props.navState.page === 1 ? 'button disabledButton' : 'button'}
                    onClick={() => { props.onPageChange(-1) }}
                    disabled={props.navState.page === 1}
                >
                    <i className="fa fa-angle-double-left auditor-icon-left" aria-hidden="true"></i>
                    Previous
                </button>
                <button
                    className={`button ${!props.items || props.navState.pageSize > getRowCount(props) ? 'disabledButton' : ''}`}
                    onClick={() => props.onPageChange(1)}
                    disabled={!props.items || props.navState.pageSize > getRowCount(props)}
                >
                    Next
                    <i className="fa fa-angle-double-right auditor-icon-right" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    );
}


function getRowCount(props) {
    if (!props.items) return 0;
    if (props.navState.startDate || props.navState.endDate) {
        return Object.keys(props.items).reduce((acc, key) => acc + props.items[key].commitEvents.length, 0)
    }
    return props.items.length;
}

export async function CommitLoader(serviceName, { request }) {
    const url = new URL(request.url);
    const qs = url.searchParams;
    const navState = {
        pageSize: qs.get('pagesize') == null ? 20 : Number(qs.get('pagesize')),
        page: qs.get('page') == null ? 1 : Number(qs.get('page')),
        asUser: qs.get('asUser'),
        commitId: qs.get('commitId'),
        buildNumber: qs.get('buildNumber'),
        controlId: qs.get('controlId'),
        boundaryCommitId: qs.get('boundaryCommitId'),
        previousPage: qs.get('previousPage') == null ? false : true,
        startDate: qs.get('startDate') ? qs.get('startDate') : null,
        endDate: qs.get('endDate') ? qs.get('endDate') : null,
        path: url.pathname,
        graphView: qs.get('graphview') === '1',
        metric: qs.get('metric') ? qs.get('metric') : '',
        process: qs.get('process') ? qs.get('process'): '',
        deployedTo: qs.get('deployedTo') ? Number(qs.get('deployedTo')) : '',
        regressionsOnly: qs.get('regressionsonly') === '1',
        farmType: qs.get('farmType') == null ? null : Number(qs.get('farmType')),
        minThreshold: qs.get('minthreshold') !== null ? Number(qs.get('minthreshold')) : null,
        minIncrease: qs.get('minincrease') !== null ? Number(qs.get('minincrease')) : null
    };

    const commits = await getCommits2(navState, serviceName);

    if (navState.graphView || navState.farmType > 5) {
        for (const commit of commits) patchCommitDetails(commit);
    }

    return { commits, navState };
}

async function getCommits2(queryParams, serviceName) {
    const apiUrl = `/api/service/${serviceName}/commits?` + commitsQueryToQueryString(queryParams, true);
    const commits = await ApiHelper<CommitDetails[]>(apiUrl);
    return commits;
}

function commitsQueryToQueryString(qp, forApi) {
    const serviceName = localStorage.getItem('selectedService');
    const qs = new URLSearchParams();
    if (forApi) {
        switch (qp.path) {
            case `/service/${serviceName}/mycommits`:
                qs.set('forCaller', 'true');
                break;
        }
    }

    qs.set('pagesize', qp.pageSize);
    if (qp.asUser != null) qs.set('asUser', qp.asUser);
    if (qp.page !== 1) {
        if (qp.previousPage) qs.set('previousPage', 'true')
        qs.set('boundaryCommitId', qp.boundaryCommitId);
        qs.set('page', qp.page.toString());
    }

    if (qp.buildNumber) qs.set('buildNumber', qp.buildNumber.toString());
    if (qp.controlId) qs.set('controlId', qp.controlId.toString());
    if (qp.startDate) qs.set('startDate', qp.startDate.toString());
    if (qp.endDate) qs.set('endDate', qp.endDate.toString());

    if (qp.startDate || qp.endDate) { //for event date search
        if (forApi) qs.set('getdetails', 'true');
    }

    if (qp.farmType != null && qp.farmType > 5 && !qp.graphView) {
        qs.set('farmType', qp.farmType.toString());
        //set the end date to today if not date is not provided
        if (!qp.endDate) qs.set('endDate', new Date().toISOString().split('T')[0]);
    }

    if (qp.graphView) {
        if (forApi) qs.set('getdetails', 'true'); else qs.set('graphview', '1');
    }

    if (qp.metric.length) {
        if (forApi) qs.set('detailsmetrictype', qp.metric); else qs.set('metric', qp.metric);
    }

    if (qp.process.length) {
        if (forApi) qs.set('process', qp.process); else qs.set('process', qp.process)
    }
    if (qp.deployedTo) {
        if (forApi) qs.set('isdeployedto', qp.deployedTo); else qs.set('deployedTo', qp.deployedTo);
    }

    if (qp.regressionsOnly) {
        if (forApi) qs.set('isregression', 'true'); else qs.set('regressionsonly', '1');
    }

    if (qp.minThreshold !== null && qp.minThreshold !== undefined) {
        qs.set('minthreshold', qp.minThreshold.toString());
    }
    if (qp.minIncrease !== null && qp.minIncrease !== undefined) {
        qs.set('minincrease', qp.minIncrease.toString());
    }

    return qs.toString();
}
